import React from 'react';
import {
  Box, TextField, Button, CircularProgress, Stack, Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import SendIcon from '@mui/icons-material/Send';
import type { Attachment } from './teachingChatUtils';
import type { PreviewState } from './teachingChatMarkdownComponents';

export interface MessageInputProps {
  input: string;
  setInput: (value: string) => void;
  sendMessage: () => void;
  isLoading: boolean;
  stopGenerating: () => void;
  abortRef: React.MutableRefObject<AbortController | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handlePaste: (e: React.ClipboardEvent<HTMLDivElement>) => void | Promise<void>;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  isDragOver: boolean;
  composeAttachments: Attachment[];
  setComposeAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  handleFiles: (files: FileList | null) => void;
  formatFileSize: (bytes: number) => string;
  setPreview: React.Dispatch<React.SetStateAction<PreviewState>>;
}

const MessageInput: React.FC<MessageInputProps> = ({
  input,
  setInput,
  sendMessage,
  isLoading,
  stopGenerating,
  fileInputRef,
  handlePaste,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  isDragOver,
  composeAttachments,
  setComposeAttachments,
  handleFiles,
  formatFileSize,
  setPreview,
}) => (
  <Box
    sx={{
      borderTop: isDragOver ? '2px dashed' : '1px solid',
      borderColor: isDragOver ? 'primary.main' : 'divider',
      p: 2,
      position: 'sticky',
      bottom: 0,
      backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
      transition: 'all 0.2s ease-in-out',
    }}
    onDragOver={handleDragOver}
    onDragLeave={handleDragLeave}
    onDrop={handleDrop}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }} onPaste={handlePaste}>
          <TextField
            fullWidth
            placeholder={
              isDragOver
                ? 'Drop files here...'
                : 'Ask for lesson plans, activities, explanations... (Paste, drag & drop, or upload .txt/.docx/.pdf/images)'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            multiline
            maxRows={4}
            disabled={isLoading}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <input
            ref={fileInputRef as React.Ref<HTMLInputElement>}
            type="file"
            accept=".txt,.docx,.pdf,image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
          {isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Button variant="outlined" color="warning" onClick={stopGenerating}>
                Stop
              </Button>
            </Stack>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={sendMessage}
                disabled={!input.trim() && composeAttachments.length === 0}
                sx={{ minWidth: 100, height: 56 }}
              >
                <SendIcon sx={{ mr: 1 }} />
                Send
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                sx={{ minWidth: 100, height: 56 }}
              >
                Upload
              </Button>
            </>
          )}
        </Box>
      </Box>
      {composeAttachments.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {composeAttachments.map((att) => {
            const getIcon = () => {
              if (att.type === 'image') return <ImageOutlinedIcon />;
              if (att.filename.toLowerCase().endsWith('.txt')) return <TextSnippetIcon />;
              if (att.filename.toLowerCase().endsWith('.docx')) return <DescriptionIcon />;
              if (att.filename.toLowerCase().endsWith('.pdf')) return <DescriptionIcon />;
              return <UploadFileIcon />;
            };

            const handleClick = () => {
              if (att.type === 'image') {
                setPreview({ open: true, src: att.data, alt: att.filename });
              } else {
                setPreview({ open: true, src: '', alt: att.filename });
              }
            };

            return (
              <Chip
                key={att.id}
                icon={getIcon()}
                label={`${att.filename}${att.fileSize ? ` • ${formatFileSize(att.fileSize)}` : ''}`}
                onClick={handleClick}
                onDelete={() => setComposeAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                variant="outlined"
                size="small"
                sx={{ maxWidth: '100%' }}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  </Box>
);

export default MessageInput;
